<script setup lang="ts">
import { computed } from 'vue'
import { useRealtime } from '@/composables/useRealtime'

interface Props {
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  showLabel: false,
  size: 'sm'
})

const { state, activeProtocol, isConnected, isConnecting, hasError, reconnect } = useRealtime()

const statusColor = computed(() => {
  switch (state.value) {
    case 'connected':
      return 'bg-green-500'
    case 'connecting':
      return 'bg-yellow-500 animate-pulse'
    case 'error':
      return 'bg-red-500'
    default:
      return 'bg-gray-500'
  }
})

const statusText = computed(() => {
  switch (state.value) {
    case 'connected':
      return `Live (${activeProtocol.value?.toUpperCase() || 'Connected'})`
    case 'connecting':
      return 'Connecting...'
    case 'error':
      return 'Connection error'
    default:
      return 'Offline'
  }
})

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'lg':
      return 'w-3 h-3'
    case 'md':
      return 'w-2.5 h-2.5'
    default:
      return 'w-2 h-2'
  }
})

const textSizeClass = computed(() => {
  switch (props.size) {
    case 'lg':
      return 'text-sm'
    case 'md':
      return 'text-xs'
    default:
      return 'text-xs'
  }
})

function handleReconnect() {
  reconnect()
}
</script>

<template>
  <div
    class="inline-flex items-center gap-2"
    role="status"
    :aria-label="`Connection status: ${statusText}`"
  >
    <span
      :class="[statusColor, sizeClasses]"
      class="rounded-full flex-shrink-0"
      aria-hidden="true"
    />
    <span
      v-if="showLabel"
      :class="[textSizeClass, isConnected ? 'text-green-400' : isConnecting ? 'text-yellow-400' : hasError ? 'text-red-400' : 'text-gray-400']"
    >
      {{ statusText }}
    </span>
    <button
      v-if="hasError || state === 'disconnected'"
      type="button"
      :class="[textSizeClass]"
      class="text-ufc-gold hover:text-ufc-gold/80 underline focus:outline-none focus:ring-2 focus:ring-ufc-gold rounded min-h-[44px] min-w-[44px] inline-flex items-center justify-center px-2"
      @click="handleReconnect"
    >
      Retry
    </button>
  </div>
</template>
